// frontend/src/pages/ManageWriters.tsx
// Phase 6c: Owner-facing management screen for Family & Friends writers.
//
// Routes: /manage-writers/:licenseId
//
// Scope:
//   - Owner-only view into memorial_writers rows for a single license.
//   - Surfaces seats used/remaining (4 seats = 5 total incl. owner).
//   - Sends invites via POST /api/memorial-writers/invite (triggers the
//     invitation email and drops a pending row into memorial_writers).
//   - Shows pending vs accepted chips so the owner can see who still needs
//     to act on the invitation in their inbox.
//
// What this page is NOT:
//   - It is not the collaborator memory-room flow that Single Tribute uses.
//     That lives in invite-collaborators.html and points to memory-room. The
//     /invite-collaborators.html page tier-branches: family_memorial owners
//     are redirected here; single_tribute owners stay on the memory-room
//     flow. Writer-management and memory-room are two distinct products that
//     happen to share the "invite collaborators" CTA entry-point.
//
// Auth / access:
//   - Clerk Bearer required for both /list and /invite.
//   - Non-owners and unrelated users land on /dashboard via the leak-proof
//     404 path returned by /list (same shape as non-existent license).
//   - Single Tribute owners hitting this page get a banner pointing them to
//     the upgrade CTA on the Dashboard (we don't silently no-op \u2014 the owner
//     explicitly navigated here and deserves to understand why it's empty).
//   - Archived / finalised licenses render read-only: seat list visible,
//     invite form disabled with an explanatory banner. The server refuses
//     /invite for these states independently; the client-side disable is
//     purely UX.
//
// Error codes handled (mirrored from api/memorial-writers/invite.ts):
//   - license_archived                    \u2192 read-only banner
//   - license_finalised                   \u2192 read-only banner
//   - not_family_memorial                 \u2192 upgrade CTA banner
//   - writer_cap_exceeded                 \u2192 inline error + disable submit
//   - http_404 on load                    \u2192 redirect /dashboard
//   - network_error / http_5xx on load    \u2192 retry button

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// ---------- Response shapes (match api/memorial-writers/list.ts + invite.ts)

interface ListWriterUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
}

interface ListWriterRow {
  id: string;
  role: string;
  invitedEmail: string | null;
  invitedAt: string;
  acceptedAt: string | null;
  status: 'accepted' | 'pending';
  user: ListWriterUser | null;
}

interface ListResponse {
  success: boolean;
  license: {
    id: string;
    deceasedName: string | null;
    tier: 'single_tribute' | 'family_memorial';
    archivedAt: string | null;
    finalisedAt: string | null;
  };
  viewer: {
    role: string;
    isOwner: boolean;
    canManage: boolean;
  };
  writers: ListWriterRow[];
  seats: {
    used: number;
    total: number;
    remaining: number;
  };
  error?: string;
}

interface InviteResult {
  email: string;
  success: boolean;
  alreadyInvited?: boolean;
  error?: string;
}

interface InviteResponse {
  success: boolean;
  message?: string;
  error?: string;
  results?: InviteResult[];
}

type LoadStatus = 'loading' | 'loaded' | 'error' | 'not_found' | 'wrong_tier';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ManageWriters: React.FC = () => {
  const { licenseId } = useParams<{ licenseId: string }>();
  const { isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [loadStatus, setLoadStatus] = useState<LoadStatus>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [listData, setListData] = useState<ListResponse | null>(null);

  // Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [inviteResults, setInviteResults] = useState<InviteResult[] | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Cyrano \u2014 Manage Writers';
    window.scrollTo(0, 0);
  }, []);

  const loadWriters = useCallback(async () => {
    if (!licenseId) {
      setLoadStatus('not_found');
      return;
    }
    setLoadStatus('loading');
    setLoadError(null);
    try {
      const authToken = await getToken();
      if (!authToken) {
        navigate(`/sign-in?redirect_url=${encodeURIComponent(`/manage-writers/${licenseId}`)}`, { replace: true });
        return;
      }
      const resp = await fetch(`/api/memorial-writers/list?licenseId=${encodeURIComponent(licenseId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = (await resp.json().catch(() => ({}))) as Partial<ListResponse>;
      if (resp.status === 404) {
        setLoadStatus('not_found');
        return;
      }
      if (!resp.ok || !data.success) {
        setLoadError((data as any)?.error || `http_${resp.status}`);
        setLoadStatus('error');
        return;
      }
      // Non-owners: the list endpoint returns writer-view rows with no
      // invitedEmail for pending invites. Owners should land here; writers
      // have no business on the management page, so send them to Dashboard.
      if (!data.viewer?.isOwner) {
        navigate('/dashboard', { replace: true });
        return;
      }
      // Wrong tier: owner is on Single Tribute. We still rendered the route,
      // so show an informative banner rather than silently redirecting.
      if (data.license?.tier !== 'family_memorial') {
        setListData(data as ListResponse);
        setLoadStatus('wrong_tier');
        return;
      }
      setListData(data as ListResponse);
      setLoadStatus('loaded');
    } catch (err: any) {
      console.error('ManageWriters load failed:', err);
      setLoadError('network_error');
      setLoadStatus('error');
    }
  }, [licenseId, getToken, navigate]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      navigate(`/sign-in?redirect_url=${encodeURIComponent(`/manage-writers/${licenseId || ''}`)}`, { replace: true });
      return;
    }
    loadWriters();
  }, [isLoaded, isSignedIn, licenseId, loadWriters, navigate]);

  const license = listData?.license;
  const seats = listData?.seats;
  const writers = listData?.writers || [];
  const isArchived = !!license?.archivedAt;
  const isFinalised = !!license?.finalisedAt;
  const isFrozen = isArchived || isFinalised;
  const canInvite =
    loadStatus === 'loaded' && !isFrozen && (seats?.remaining ?? 0) > 0;

  const remainingForDisplay = useMemo(() => {
    if (!seats) return 0;
    return Math.max(0, seats.remaining);
  }, [seats]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!licenseId) return;

    setFormError(null);
    setInviteResults(null);
    setSuccessBanner(null);

    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedMessage = personalMessage.trim();

    if (!EMAIL_RE.test(trimmedEmail)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (!canInvite) {
      setFormError('No seats remaining for new writers on this memorial.');
      return;
    }

    setSubmitting(true);
    try {
      const authToken = await getToken();
      if (!authToken) {
        navigate(`/sign-in?redirect_url=${encodeURIComponent(`/manage-writers/${licenseId}`)}`, { replace: true });
        return;
      }
      const resp = await fetch('/api/memorial-writers/invite', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          licenseId,
          invitees: [{ email: trimmedEmail, name: trimmedName || undefined }],
          personalMessage: trimmedMessage || undefined,
        }),
      });
      const data = (await resp.json().catch(() => ({}))) as InviteResponse;

      if (!resp.ok || !data.success) {
        const code = data?.error;
        if (code === 'not_family_memorial') {
          setFormError('This piece is on Single Tribute. Upgrade to Family & Friends on the Dashboard to invite writers.');
        } else if (code === 'license_archived') {
          setFormError('The 90-day writing window has closed. Inviting new writers is no longer available.');
        } else if (code === 'license_finalised') {
          setFormError('This piece has been finalised. Inviting new writers is no longer available.');
        } else if (code === 'writer_cap_exceeded') {
          setFormError(data.message || 'No seats remaining for new writers on this memorial.');
        } else {
          setFormError(data.message || data.error || 'We couldn\u2019t send that invitation. Please try again.');
        }
        setInviteResults(data.results || null);
        return;
      }

      // Surface per-email results so "already invited" shows without looking
      // like a failure. The happy path is one invite at a time here.
      setInviteResults(data.results || null);
      setSuccessBanner(data.message || 'Invitation sent.');
      setEmail('');
      setName('');
      setPersonalMessage('');
      // Refresh the writers list so the new pending chip appears immediately.
      await loadWriters();
    } catch (err: any) {
      console.error('ManageWriters invite failed:', err);
      setFormError('We couldn\u2019t reach the server. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Loading skeleton

  if (!isLoaded || loadStatus === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#eae8e4' }}>
        <Header />
        <main style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 20px' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '3px solid #eae8e4',
                borderTopColor: '#19285c',
                borderRadius: '50%',
                animation: 'spin 0.9s linear infinite',
                margin: '0 auto 16px',
              }}
            />
            <p style={{ fontSize: '15px', color: '#19285c', margin: 0 }}>Loading writers\u2026</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------- Leak-proof 404 redirect fallback (covers direct URL pokes)

  if (loadStatus === 'not_found') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#eae8e4' }}>
        <Header />
        <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '48px 20px' }}>
          <div style={cardStyle}>
            <h1 style={titleStyle}>We couldn\u2019t find that piece</h1>
            <p style={bodyStyle}>
              This memorial may have been archived, or the link may belong to a piece you\u2019re not part of.
              Head back to the Dashboard to see the pieces you own or have been invited to.
            </p>
            <button type="button" onClick={() => navigate('/dashboard')} style={primaryButtonStyle}>
              Go to Dashboard
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------- Transient load error (network / 5xx) \u2014 retry path

  if (loadStatus === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#eae8e4' }}>
        <Header />
        <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '48px 20px' }}>
          <div style={cardStyle}>
            <h1 style={titleStyle}>We couldn\u2019t load the writers</h1>
            <p style={bodyStyle}>
              {loadError === 'network_error'
                ? 'We couldn\u2019t reach the server. Please check your connection and try again.'
                : 'Something went wrong loading this memorial. Please try again in a moment.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button type="button" onClick={() => loadWriters()} style={primaryButtonStyle}>
                Try again
              </button>
              <button type="button" onClick={() => navigate('/dashboard')} style={secondaryButtonStyle}>
                Go to Dashboard
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------- Wrong tier (owner on Single Tribute) \u2014 surface upgrade CTA

  if (loadStatus === 'wrong_tier') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#eae8e4' }}>
        <Header />
        <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '48px 20px' }}>
          <div style={cardStyle}>
            <h1 style={titleStyle}>Writers are a Family &amp; Friends feature</h1>
            <p style={bodyStyle}>
              {license?.deceasedName ? (
                <>
                  The memorial for <strong style={{ color: '#19285c' }}>{license.deceasedName}</strong> is on the
                  Single Tribute tier, which is a single-writer experience. Upgrade to Family &amp; Friends Group
                  Memorial to invite up to four other writers to contribute alongside you.
                </>
              ) : (
                <>
                  This memorial is on the Single Tribute tier. Upgrade to Family &amp; Friends Group Memorial to
                  invite up to four other writers to contribute alongside you.
                </>
              )}
            </p>
            <div style={infoBoxStyle}>
              <p style={infoBoxLabelStyle}>What upgrade includes</p>
              <p style={infoBoxBodyStyle}>
                Up to five writers total (you plus four), real-time collaboration as we roll it out, and a
                shared Memory Collection PDF at finalisation. Your existing work is preserved \u2014 nothing gets
                reset by the upgrade.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button type="button" onClick={() => navigate('/dashboard')} style={primaryButtonStyle}>
                Go to Dashboard to upgrade
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------- Loaded: render the management surface

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#eae8e4' }}>
      <Header />
      <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: '720px' }}>
          {/* HEADER CARD */}
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '32px 28px',
              boxShadow: '0 4px 24px rgba(25, 40, 92, 0.08)',
              border: '1px solid rgba(25, 40, 92, 0.06)',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
              <img
                src="/Assets/200-handdrawn-icons-by-heloholo/png/64/add-contact64.png"
                alt=""
                style={{ width: '44px', height: '44px', opacity: 0.85, flexShrink: 0 }}
              />
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    fontSize: '12px',
                    color: '#8c83b4',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    margin: '0 0 4px 0',
                  }}
                >
                  Family &amp; Friends \u2014 Writers
                </p>
                <h1
                  style={{
                    fontFamily: 'Playfair Display, Georgia, serif',
                    fontSize: '26px',
                    fontWeight: 600,
                    color: '#19285c',
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  {license?.deceasedName
                    ? `Writers for ${license.deceasedName}`
                    : 'Writers for this memorial'}
                </h1>
              </div>
            </div>
            <p style={{ fontSize: '14px', color: '#8c83b4', margin: '0 0 18px 0', lineHeight: 1.55 }}>
              Invite up to four family members or friends to write alongside you. Each person gets an email
              invitation and lands in Cyrano when they accept.
            </p>

            {/* Seat counter */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: '#eae8e4',
                borderRadius: '10px',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: '12px',
                    color: '#8c83b4',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    margin: '0 0 2px 0',
                  }}
                >
                  Seats used
                </p>
                <p style={{ fontSize: '15px', color: '#131e40', margin: 0, fontWeight: 500 }}>
                  {seats?.used ?? 0} of {seats?.total ?? 4} invited
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p
                  style={{
                    fontSize: '12px',
                    color: '#8c83b4',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    margin: '0 0 2px 0',
                  }}
                >
                  Remaining
                </p>
                <p style={{ fontSize: '15px', color: '#19285c', margin: 0, fontWeight: 600 }}>
                  {remainingForDisplay}
                </p>
              </div>
            </div>

            {/* Frozen-state banner */}
            {isFrozen && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '14px 16px',
                  backgroundColor: '#eae8e4',
                  borderLeft: '3px solid #d4adbb',
                  borderRadius: '10px',
                }}
              >
                <p style={{ fontSize: '13px', color: '#131e40', margin: 0, lineHeight: 1.55 }}>
                  {isFinalised
                    ? 'This piece has been finalised. You can still see who contributed, but new invitations are no longer available.'
                    : 'The 90-day writing window has closed and this piece has been archived. New invitations are no longer available, but existing writers can still read what\u2019s been written.'}
                </p>
              </div>
            )}
          </div>

          {/* WRITERS LIST CARD */}
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '28px',
              boxShadow: '0 4px 24px rgba(25, 40, 92, 0.08)',
              border: '1px solid rgba(25, 40, 92, 0.06)',
              marginBottom: '20px',
            }}
          >
            <h2
              style={{
                fontFamily: 'Playfair Display, Georgia, serif',
                fontSize: '18px',
                fontWeight: 600,
                color: '#19285c',
                margin: '0 0 14px 0',
              }}
            >
              Current writers
            </h2>
            {writers.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#8c83b4', margin: 0, lineHeight: 1.55 }}>
                No writers yet. Invite someone below to start collaborating on this memorial.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {writers.map((w) => (
                  <li
                    key={w.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 14px',
                      backgroundColor: '#ffffff',
                      border: '1px solid rgba(25, 40, 92, 0.08)',
                      borderLeft: `3px solid ${w.status === 'accepted' ? '#19285c' : '#d4adbb'}`,
                      borderRadius: '10px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#131e40',
                          margin: '0 0 2px 0',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {w.user?.displayName || w.invitedEmail || 'Invited writer'}
                      </p>
                      {w.invitedEmail && w.user?.displayName && (
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#8c83b4',
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {w.invitedEmail}
                        </p>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        backgroundColor: w.status === 'accepted' ? '#19285c' : '#d4adbb',
                        color: w.status === 'accepted' ? '#ffffff' : '#19285c',
                        flexShrink: 0,
                      }}
                    >
                      {w.status === 'accepted' ? 'Accepted' : 'Pending'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* INVITE FORM CARD */}
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '28px',
              boxShadow: '0 4px 24px rgba(25, 40, 92, 0.08)',
              border: '1px solid rgba(25, 40, 92, 0.06)',
              marginBottom: '20px',
              opacity: canInvite ? 1 : 0.7,
            }}
          >
            <h2
              style={{
                fontFamily: 'Playfair Display, Georgia, serif',
                fontSize: '18px',
                fontWeight: 600,
                color: '#19285c',
                margin: '0 0 14px 0',
              }}
            >
              Invite a writer
            </h2>

            {!canInvite && !isFrozen && (
              <div
                style={{
                  marginBottom: '14px',
                  padding: '12px 14px',
                  backgroundColor: '#eae8e4',
                  borderLeft: '3px solid #d4adbb',
                  borderRadius: '10px',
                }}
              >
                <p style={{ fontSize: '13px', color: '#131e40', margin: 0, lineHeight: 1.55 }}>
                  You\u2019ve reached the four-writer invite cap for Family &amp; Friends. If someone can no longer
                  contribute, email{' '}
                  <a href="mailto:support@cyrano.cloud" style={{ color: '#19285c' }}>
                    support@cyrano.cloud
                  </a>{' '}
                  to have their invite revoked so you can invite someone new.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label htmlFor="writer-email" style={labelStyle}>
                  Email <span style={{ color: '#d4adbb' }}>*</span>
                </label>
                <input
                  id="writer-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!canInvite || submitting}
                  placeholder="writer@example.com"
                  style={inputStyle}
                  autoComplete="off"
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label htmlFor="writer-name" style={labelStyle}>
                  Name (optional)
                </label>
                <input
                  id="writer-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canInvite || submitting}
                  placeholder="Sarah Thompson"
                  maxLength={100}
                  style={inputStyle}
                  autoComplete="off"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="writer-message" style={labelStyle}>
                  Personal note (optional)
                </label>
                <textarea
                  id="writer-message"
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  disabled={!canInvite || submitting}
                  placeholder="A short note to include in the invitation email\u2026"
                  maxLength={1000}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                />
                <p style={{ fontSize: '12px', color: '#8c83b4', margin: '4px 0 0 0' }}>
                  {personalMessage.length}/1000
                </p>
              </div>

              {formError && (
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '12px 14px',
                    backgroundColor: '#fdf0f3',
                    borderLeft: '3px solid #d4adbb',
                    borderRadius: '10px',
                  }}
                >
                  <p style={{ fontSize: '13px', color: '#131e40', margin: 0, lineHeight: 1.5 }}>{formError}</p>
                </div>
              )}

              {successBanner && (
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '12px 14px',
                    backgroundColor: '#eae8e4',
                    borderLeft: '3px solid #19285c',
                    borderRadius: '10px',
                  }}
                >
                  <p style={{ fontSize: '13px', color: '#131e40', margin: 0, lineHeight: 1.5 }}>{successBanner}</p>
                </div>
              )}

              {inviteResults && inviteResults.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {inviteResults.map((r, i) => (
                    <li
                      key={`${r.email}-${i}`}
                      style={{
                        fontSize: '13px',
                        color: r.success ? '#19285c' : '#131e40',
                        padding: '8px 12px',
                        backgroundColor: r.success ? '#eae8e4' : '#fdf0f3',
                        borderRadius: '8px',
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>{r.email}</strong> \u2014{' '}
                      {r.success
                        ? r.alreadyInvited
                          ? 'already invited'
                          : 'invitation sent'
                        : r.error || 'could not send'}
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="submit"
                disabled={!canInvite || submitting}
                style={{
                  ...primaryButtonStyle,
                  width: '100%',
                  opacity: !canInvite || submitting ? 0.6 : 1,
                  cursor: !canInvite || submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Sending\u2026' : 'Send invitation'}
              </button>
            </form>
          </div>

          {/* BOTTOM NAV */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'none',
                border: 'none',
                color: '#19285c',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '8px 12px',
              }}
            >
              &larr; Back to Dashboard
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// ---------- Shared style tokens (match AcceptWriterInvite.tsx)

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '560px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '40px 32px',
  boxShadow: '0 4px 24px rgba(25, 40, 92, 0.08)',
  border: '1px solid rgba(25, 40, 92, 0.06)',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'Playfair Display, Georgia, serif',
  fontSize: '26px',
  fontWeight: 600,
  color: '#19285c',
  margin: '0 0 12px 0',
  lineHeight: 1.3,
};

const bodyStyle: React.CSSProperties = {
  fontSize: '15px',
  lineHeight: 1.6,
  color: '#131e40',
  marginTop: 0,
  marginBottom: '20px',
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#19285c',
  color: '#ffffff',
  padding: '14px 20px',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  minHeight: '44px',
};

const secondaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  color: '#19285c',
  padding: '14px 20px',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: 600,
  border: '1.5px solid #19285c',
  cursor: 'pointer',
  minHeight: '44px',
};

const infoBoxStyle: React.CSSProperties = {
  backgroundColor: '#eae8e4',
  borderRadius: '10px',
  padding: '16px 18px',
  margin: '16px 0 20px',
  borderLeft: '3px solid #d4adbb',
};

const infoBoxLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#8c83b4',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  margin: '0 0 6px 0',
};

const infoBoxBodyStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#131e40',
  margin: 0,
  lineHeight: 1.55,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  color: '#131e40',
  fontWeight: 600,
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  fontSize: '15px',
  color: '#131e40',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(25, 40, 92, 0.15)',
  borderRadius: '10px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  minHeight: '44px',
  outline: 'none',
};

export default ManageWriters;
